// Route of a day on the planning maps (WidgetMyDay and the agenda's CalendarMap share these rules, #23640).
// Points are [lng, lat], positions { lat, lng } as MapLibre's getUserPosition() returns them.

export const ROUTE_LEG_STATUS = { VALIDATED: 'validated', IN_PROGRESS: 'inProgress', TODO: 'todo' };

export const ROUTE_LEG_COLORS = {
	[ROUTE_LEG_STATUS.VALIDATED]: '#4CAF50',
	[ROUTE_LEG_STATUS.IN_PROGRESS]: '#FF9800',
	[ROUTE_LEG_STATUS.TODO]: '#1976D2',
};

export const positionToPoint = (position) => (position?.lat && position?.lng ? [position.lng, position.lat] : null);
export const pointToPosition = (point) => (point ? { lng: point[0], lat: point[1] } : null);

const samePoint = (a, b) => !!a && !!b && a[0] === b[0] && a[1] === b[1];
const appendDistinct = (points, point) => !samePoint(points[points.length - 1], point) && points.push(point);

/** [lng, lat] of a delivery location from the DELIVERYLOCATIONGEO storage entries, null without coordinates. */
export function geoPointLookup(geoInfos) {
	const points = new Map();
	(geoInfos || []).forEach((g) => g.latitude && g.longitude && !points.has(g.deliveryLocationId) && points.set(g.deliveryLocationId, [g.longitude, g.latitude]));
	return (deliveryLocationId) => points.get(deliveryLocationId) || null;
}

/** An HC appointment can be done from anywhere: only client visits and nights (HC of subtype NIGHT) are on the route. */
export function isRoutableAppointment(appointment, enumAppointment) {
	if (!appointment || appointment.status === enumAppointment.status.CANCELLED) return false;
	return appointment.type === enumAppointment.type.CL || appointment.subType === enumAppointment.getSubTypeId('NIGHT');
}

function getStopStatus(appointment, enumAppointment) {
	if (appointment.status === enumAppointment.status.VALIDATED) return ROUTE_LEG_STATUS.VALIDATED;
	if (appointment.status === enumAppointment.status.INPROGRESS) return ROUTE_LEG_STATUS.IN_PROGRESS;
	return ROUTE_LEG_STATUS.TODO;
}

/**
 * Stops of a day and where its route starts and ends. The day starts at the night it wakes up from (a NIGHT that
 * began before `dayStart`), else at home when the Routing module counts the first route (EnableFirstRoute), else at
 * its first stop. It ends at home when EnableLastRoute is set, unless its last stop is a night.
 *
 * @param {object} options
 * @param {object[]} options.appointments  appointments of the day in time order, of any type (filtered here)
 * @param {Date} options.dayStart  midnight of the day
 * @param {(deliveryLocationId) => [number, number]|null} options.getPoint
 * @param {*} options.homeId  delivery location of the user's home, never a stop
 * @param {{ enableFirstRoute: boolean, enableLastRoute: boolean }} options.routing  sfaHelper.getRouting()
 * @param {object} options.enumAppointment
 * @returns {{ stops: { appointment: object, point: [number, number], status: string }[], start: [number, number]|null, end: [number, number]|null, fallbackStart: [number, number]|null }}
 *   fallbackStart = the night or home whatever the settings, where a route to the first stop starts without geolocation
 */
export function getDayRouteStops({ appointments, dayStart, getPoint, homeId, routing, enumAppointment }) {
	const nightSubTypeId = enumAppointment.getSubTypeId('NIGHT');
	const routable = (appointments || []).filter((a) => isRoutableAppointment(a, enumAppointment) && a.deliveryLocationId !== homeId);
	const startNight = routable[0]?.subType === nightSubTypeId && new Date(routable[0].startDate) < dayStart ? routable[0] : null;
	const visits = startNight ? routable.slice(1) : routable;
	const home = homeId ? getPoint(homeId) : null;
	const night = startNight ? getPoint(startNight.deliveryLocationId) : null;
	const endsWithNight = visits.length > 0 && visits[visits.length - 1].subType === nightSubTypeId;

	return {
		stops: visits.map((appointment) => ({ appointment, point: getPoint(appointment.deliveryLocationId), status: getStopStatus(appointment, enumAppointment) })).filter((stop) => stop.point),
		start: night || (routing?.enableFirstRoute ? home : null),
		end: endsWithNight ? null : routing?.enableLastRoute ? home : null,
		fallbackStart: night || home,
	};
}

/**
 * Route of the whole day, start → stops → end, each leg colored by the status of the stop it leads to (validated,
 * in progress, to do). Only the in-progress and to-do legs count in the distance and duration shown on the map.
 * With an `origin` (the live position, today), what is left of the day starts from it rather than from the previous
 * stop: the hop from that stop to the position is not drawn.
 *
 * @returns {{ waypoints: [number, number][], legs: ({ status: string, color: string, counted: boolean }|null)[], sequence: [number, number][], navigation: [number, number][] }}
 *   legs[i] joins waypoints[i] to waypoints[i + 1] (null = not drawn), sequence = the day's points without the live
 *   position, navigation = the stops left to drive to, for the external navigation app, which starts from the device's
 *   position (#23654)
 */
export function planDayRoute({ stops = [], start = null, end = null, origin = null }) {
	const points = [...(start ? [{ point: start, status: null }] : []), ...stops, ...(end ? [{ point: end, status: ROUTE_LEG_STATUS.TODO }] : [])];
	const firstRemaining = points.findIndex((p) => p.status && p.status !== ROUTE_LEG_STATUS.VALIDATED);
	const waypoints = [];
	const legs = [];
	const sequence = [];
	const navigation = [];

	const push = (point, leg) => {
		if (samePoint(waypoints[waypoints.length - 1], point)) return;
		if (waypoints.length) legs.push(leg);
		waypoints.push(point);
	};

	points.forEach((p, i) => {
		if (i === firstRemaining && origin) push(origin, null);
		push(p.point, p.status ? { status: p.status, color: ROUTE_LEG_COLORS[p.status], counted: p.status !== ROUTE_LEG_STATUS.VALIDATED } : null);
		appendDistinct(sequence, p.point);
		if (firstRemaining >= 0 && i >= firstRemaining && p.status !== ROUTE_LEG_STATUS.VALIDATED) appendDistinct(navigation, p.point);
	});

	return { waypoints, legs, sequence, navigation };
}

/**
 * Next stop of the day, its first stop not validated, and where to start from without geolocation: the previous
 * stop, else where the day starts (night, else home).
 */
export function getNextStop({ stops = [], fallbackStart = null }) {
	const index = stops.findIndex((stop) => stop.status !== ROUTE_LEG_STATUS.VALIDATED);
	if (index < 0) return null;
	return { ...stops[index], fallbackOrigin: index > 0 ? stops[index - 1].point : fallbackStart };
}
